import { Box, Chip, Grid, Stack, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridOverlay } from '@mui/x-data-grid'
import React from 'react'

import { useTranslation } from 'hooks/use-translation'
import { DatadogMonitor } from 'types/api/datadog/monitors'

const EmptyState: React.FC = () => {
  const { trans } = useTranslation()

  return (
    <GridOverlay>
      <Typography>{trans('status_checks.monitors.list.empty_text')}</Typography>
    </GridOverlay>
  )
}

const NoneFoundState: React.FC = () => {
  const { trans } = useTranslation()

  return (
    <GridOverlay>
      <Typography>{trans('status_checks.monitors.list.no_results')}</Typography>
    </GridOverlay>
  )
}

const ErrorState: React.FC<{ error: string }> = ({ error }) => (
  <GridOverlay>
    <Typography>{error}</Typography>
  </GridOverlay>
)

export const MonitorSearchList: React.FC<{
  hasSearched?: boolean
  monitors: DatadogMonitor[]
  onClick(id: string): void
  isLoading?: boolean
  errorMessage?: string
}> = ({
  hasSearched = false,
  monitors = [],
  onClick,
  isLoading = false,
  errorMessage
}) => {
  const { trans } = useTranslation()

  const tableColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: trans('status_checks.monitors.list.headers.name'),
      flex: 2,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      disableReorder: true,
      renderCell: (params) => {
        return (
          <Stack alignItems="flex-start" direction="column" width="100%">
            <Typography mt={1} variant="body2">
              {params.row.name}
            </Typography>
            <Grid container gap={1} justifyContent="end" p={1} width="100%">
              {params.row.tags.map((tag) => (
                <Grid key={tag} item>
                  <Chip css={{ opacity: 0.7 }} label={tag} size="small" />
                </Grid>
              ))}
            </Grid>
          </Stack>
        )
      }
    }
  ]

  return (
    <Box data-test-status-check-search-list>
      <DataGrid
        autoHeight
        data-test-status-check-search-list
        hideFooterSelectedRowCount
        columns={tableColumns}
        components={{
          NoRowsOverlay: () => {
            if (errorMessage) {
              return <ErrorState error={errorMessage} />
            }
            if (hasSearched) {
              return <NoneFoundState />
            }
            return <EmptyState />
          }
        }}
        getRowHeight={() => 'auto'}
        getRowId={(row) => row.id}
        loading={isLoading && !errorMessage}
        pageSize={10}
        rows={monitors}
        rowsPerPageOptions={[10]}
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 0
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none'
          },
          border: 'none'
        }}
        onCellClick={(params) => {
          onClick(params.row.id)
        }}
      />
    </Box>
  )
}
